import { AnyTreeNode, FolderNode, VisibleTreeNode } from './types';

export const isFolder = <F, L>(
  node: AnyTreeNode<F, L>,
): node is FolderNode<F, L> => node.kind === 'folder';

export const findNodeById = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  id: string,
): AnyTreeNode<F, L> | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    if (isFolder(node)) {
      const found = findNodeById(node.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const walkTree = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  callback: (node: AnyTreeNode<F, L>, depth: number) => void,
  depth = 0,
): void => {
  for (const node of nodes) {
    callback(node, depth);
    if (isFolder(node)) {
      walkTree(node.children, callback, depth + 1);
    }
  }
};

const matchesQuery = (label: string, q: string): boolean =>
  label.toLowerCase().includes(q.toLowerCase());

const sortFoldersFirst = <F, L>(
  nodes: AnyTreeNode<F, L>[],
): AnyTreeNode<F, L>[] => {
  const folders: AnyTreeNode<F, L>[] = [];
  const leaves: AnyTreeNode<F, L>[] = [];

  for (const node of nodes) {
    if (isFolder(node)) {
      folders.push({ ...node, children: sortFoldersFirst(node.children) });
    } else {
      leaves.push(node);
    }
  }

  return [...folders, ...leaves];
};

export const filterTree = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  query: string,
  predicate?: (node: AnyTreeNode<F, L>) => boolean,
  getSearchText?: (node: AnyTreeNode<F, L>) => string,
): { nodes: AnyTreeNode<F, L>[]; forceExpand: Set<string> } => {
  const forceExpand = new Set<string>();
  const hasQuery = query.trim().length > 0;
  const hasPredicate = typeof predicate === 'function';

  if (!hasQuery && !hasPredicate) {
    return { nodes: sortFoldersFirst(nodes), forceExpand };
  }

  const filter = (
    list: AnyTreeNode<F, L>[],
    ancestors: string[],
  ): AnyTreeNode<F, L>[] => {
    const out: AnyTreeNode<F, L>[] = [];
    for (const node of list) {
      const isPredicateOk = !hasPredicate || predicate!(node);
      if (!isPredicateOk) {
        continue;
      }
      const searchable = getSearchText?.(node) ?? node.label;
      const isSelfMatch = !hasQuery || matchesQuery(searchable, query);
      if (isFolder(node)) {
        const childAncestors = [...ancestors, node.id];
        const filteredChildren = filter(node.children, childAncestors);
        const hasMatchedChild = filteredChildren.length > 0;
        if (isSelfMatch || hasMatchedChild) {
          if (hasQuery && (hasMatchedChild || isSelfMatch)) {
            ancestors.forEach((id) => forceExpand.add(id));
            forceExpand.add(node.id);
          }
          out.push({ ...node, children: filteredChildren });
        }
      } else if (isSelfMatch) {
        if (hasQuery) {
          ancestors.forEach((id) => forceExpand.add(id));
        }
        out.push(node);
      }
    }
    return out;
  };

  return { nodes: sortFoldersFirst(filter(nodes, [])), forceExpand };
};

export const flattenVisibleNodes = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  expandedIds: ReadonlySet<string>,
): VisibleTreeNode<F, L>[] => {
  const out: VisibleTreeNode<F, L>[] = [];
  const visit = (
    list: AnyTreeNode<F, L>[],
    depth: number,
    ancestorIds: string[],
    ancestorIsLast: boolean[],
  ) => {
    list.forEach((node, index) => {
      const isLast = index === list.length - 1;
      out.push({ node, depth, ancestorIds, isLast, ancestorIsLast });
      if (isFolder(node) && expandedIds.has(node.id)) {
        visit(
          node.children,
          depth + 1,
          [...ancestorIds, node.id],
          [...ancestorIsLast, isLast],
        );
      }
    });
  };
  visit(nodes, 0, [], []);
  return out;
};

export const toggleInSet = <T>(set: ReadonlySet<T>, value: T): Set<T> => {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

export const collectFolderIds = <F, L>(
  nodes: AnyTreeNode<F, L>[],
  maxDepth?: number,
): Set<string> => {
  const out = new Set<string>();
  walkTree(nodes, (node, depth) => {
    if (isFolder(node) && (maxDepth === undefined || depth < maxDepth)) {
      out.add(node.id);
    }
  });
  return out;
};
